import jsPDF from 'jspdf';
import 'jspdf-autotable';
import { getPurchaseOrder, getPurchaseOrderItems } from '../api/procurement';
import { getVendor } from '../api/vendor';
import { getUoms } from '../api/inventoryMaster';
import { formatDate, formatQty } from '../utils';
import { fetchItemCatalog, normalizeItemList, resolveItemDisplayName } from './itemDisplay';
import { HOSPITAL_INFO } from '../constants';
import { resolveFacilityIdentity, formatRegistrationLine } from './facilityIdentity';
import {
  C,
  SIDE_M,
  U,
  CARD_W,
  CARD_X_RIGHT,
  CARD_HEAD_H,
  CARD_PAD_BOT,
  CARD_GAP,
  CONT_TOP,
  DASH,
  Rs,
  pct,
  numberToWords,
  displayOrDash,
  drawLetterhead,
  drawTitleRow,
  measureRows,
  measureLines,
  drawCardFrame,
  drawCardTitle,
  strokeCard,
  drawRows,
  drawLines,
  measureSummary,
  drawSummaryCard,
  measureWordsCard,
  drawWordsCard,
  tableStyles,
  drawRowRule,
  drawItemSubline,
  ensureSpace,
  drawContinuationHeader,
  drawSignatureRow,
  stampFooters,
} from './pdfTheme';

// PO status vocabulary. In v5 these colour the chip's border and label rather than
// filling it, so they double as the print-safe status indicator.
const STATUS_BADGE_COLORS = {
  DRAFT:            [120, 120, 128],
  PENDING_APPROVAL: [180, 120,   0],
  SUBMITTED:        [180, 120,   0],
  APPROVED:         [ 22, 163,  74],
  SENT_TO_VENDOR:   [ 37,  99, 235],
  CLOSED:           [ 15,  76,  92],
  CANCELLED:        [220,  38,  38],
};

function badgeColor(status) { return STATUS_BADGE_COLORS[status] || [120, 120, 128]; }
function badgeLabel(status) { return (status || '').replace(/_/g, ' ') || DASH; }

// ─── Item enrichment ──────────────────────────────────────────────────────────
function enrichItems(items, catalog, uomMap = {}) {
  return items.map(it => {
    const ci = catalog?.byId?.get(it.item_id || it.id) || null;
    return {
      ...it,
      item_name:         resolveItemDisplayName(it, catalog),
      item_code:         it.item_code || ci?.item_code || ci?.item_number || ci?.sku || DASH,
      hsn_code:          it.hsn_code  || ci?.hsn_code || DASH,
      uom_display:       it.uom_name  || it.uom || uomMap[it.uom_id] || DASH,
      mrp:               Number(it.mrp || it.maximum_retail_price || 0) || null,
      free_quantity:     Number(it.free_quantity || it.free_qty || 0),
      manufacturer_name: it.manufacturer_name || it.manufacturer || ci?.manufacturer_name || ci?.manufacturer || null,
      cgst_percent:      Number(it.cgst_percent || it.cgst_rate || 0),
      sgst_percent:      Number(it.sgst_percent || it.sgst_rate || 0),
      igst_percent:      Number(it.igst_percent || it.igst_rate || 0),
      cgst_amount:       Number(it.cgst_amount  || it.cgst_amt  || 0),
      sgst_amount:       Number(it.sgst_amount  || it.sgst_amt  || 0),
      igst_amount:       Number(it.igst_amount  || it.igst_amt  || 0),
    };
  });
}

// ─── Core builder ─────────────────────────────────────────────────────────────
export function createPoPdfDocument({
  po = {},
  items = [],
  vendor = null,
  facilityLogo = null,
  // Prints the hospital letterhead unless the caller asked for a blank header, for
  // pre-printed stationery. The layout is identical either way.
  letterhead = true,
} = {}) {
  const doc = new jsPDF({ unit: 'mm', format: 'a4' });
  const identity = resolveFacilityIdentity();
  const registration = formatRegistrationLine(identity);
  const onNewPage = (d) =>
    drawContinuationHeader(d, identity, 'Purchase Order', po.po_number, facilityLogo, letterhead);

  // ── 1. Letterhead + title ──────────────────────────────────────────────────
  let y = drawLetterhead(doc, identity, registration, facilityLogo, letterhead);
  y = drawTitleRow(doc, y, {
    title: 'PURCHASE ORDER',
    subtitle: 'Original for Vendor  ·  Procurement Document',
    chipLabel: badgeLabel(po.status),
    chipColor: badgeColor(po.status),
  });

  // ── 2. PO INFORMATION + VENDOR INFORMATION ─────────────────────────────────
  // The five rows the design specifies always render (with an em dash when empty,
  // as the design shows). The two extra fields this module has always printed are
  // appended only when they carry data, so nothing is lost and nothing is invented.
  const leftRows = [
    ['PO Number',     po.po_number],
    ['PO Date',       formatDate(po.po_date || po.created_at)],
    ['Reference',     po.ref_document || po.ref_doc || po.reference_document],
    ['Delivery Date', formatDate(po.expected_delivery_date)],
    ['Payment Terms', po.payment_terms],
  ];
  const quotationNo = po.quotation_number || po.rfq_number || po.quote_no;
  if (quotationNo) leftRows.splice(3, 0, ['Quotation No', quotationNo]);
  const placeOfDelivery = po.place_of_delivery || po.delivery_place || po.delivery_location;
  if (placeOfDelivery) leftRows.push(['Place of Delivery', placeOfDelivery]);

  const vendorAddress =
    vendor?.address || [vendor?.city, vendor?.state].filter(Boolean).join(', ');
  const vendorContact = vendor?.contact_person
    ? `${vendor.contact_person}${vendor.contact_phone ? `  ·  ${vendor.contact_phone}` : ''}`
    : vendor?.phone;

  const rightRows = [
    ['Vendor',      vendor?.vendor_name],
    ['Vendor Code', vendor?.vendor_code],
    ['Address',     vendorAddress],
    ['GSTIN',       vendor?.gstin],
    ['Contact',     vendorContact],
  ];
  if (vendor?.email) rightRows.push(['Email', vendor.email]);

  const leftM = measureRows(doc, leftRows, CARD_W);
  const rightM = measureRows(doc, rightRows, CARD_W);
  const infoH = Math.max(leftM.height, rightM.height);

  y = ensureSpace(doc, y, infoH, onNewPage);
  drawRows(doc, SIDE_M, drawCardFrame(doc, SIDE_M, y, CARD_W, infoH, 'Purchase Order Information'),
    CARD_W, leftRows, leftM.counts);
  drawRows(doc, CARD_X_RIGHT, drawCardFrame(doc, CARD_X_RIGHT, y, CARD_W, infoH, 'Vendor Information'),
    CARD_W, rightRows, rightM.counts);
  y += infoH + CARD_GAP;

  // ── 3. BUYER / SHIP TO ─────────────────────────────────────────────────────
  const buyerText = [
    [identity.name, po.delivery_address || po.shipping_address || identity.address]
      .filter(Boolean)
      .join('  ·  '),
    registration,
  ]
    .filter(Boolean)
    .join('\n');

  const buyerM = measureLines(doc, buyerText, U);
  y = ensureSpace(doc, y, buyerM.height, onNewPage);
  drawLines(
    doc,
    SIDE_M,
    drawCardFrame(doc, SIDE_M, y, U, buyerM.height, 'Buyer / Ship To'),
    buyerM.lines,
    buyerM.pitch,
  );
  y += buyerM.height + CARD_GAP;

  // ── 4. ITEMS ───────────────────────────────────────────────────────────────
  // Optional commercial columns are dropped when no line uses them — that is what
  // produces the design's lean seven-column table, and it hands the width back to
  // the description instead of printing a column of em dashes.
  const showMrp = items.some(it => Number(it.mrp) > 0);
  const showDisc = items.some(it => Number(it.discount_percent) > 0);

  const columns = [
    { key: 'sl',   label: '#',                w: 6.5,    halign: 'center', bold: true },
    { key: 'desc', label: 'ITEM DESCRIPTION', w: 'auto' },
    { key: 'uom',  label: 'UOM',              w: 13,     halign: 'center' },
    { key: 'qty',  label: 'QTY',              w: 18,     halign: 'right' },
    ...(showMrp ? [{ key: 'mrp', label: 'MRP', w: 22, halign: 'right' }] : []),
    { key: 'rate', label: 'UNIT RATE',        w: 24,     halign: 'right' },
    ...(showDisc ? [{ key: 'disc', label: 'DISC%', w: 12, halign: 'right' }] : []),
    { key: 'gst',  label: 'GST%',             w: 12,     halign: 'right' },
    { key: 'amt',  label: 'AMOUNT',           w: 26,     halign: 'right', bold: true },
  ];
  const descIndex = columns.findIndex(c => c.key === 'desc');

  let totalGross = 0;
  let totalDiscount = 0;
  let totalTax = 0;
  let hasDiscount = false;
  const cgstBuckets = {};
  const sgstBuckets = {};
  const igstBuckets = {};

  const tableRows = items.map((it, i) => {
    const qty     = Number(it.ordered_quantity) || 0;
    const freeQty = Number(it.free_quantity)    || 0;
    const rate    = Number(it.unit_rate)        || 0;
    const disc    = Number(it.discount_percent) || 0;
    const tax     = Number(it.tax_percent)      || 0;
    const gross   = qty * rate;
    const discAmt = gross * (disc / 100);
    const basic   = gross - discAmt;
    const taxAmt  = basic * (tax / 100);

    totalGross    += gross;
    totalDiscount += discAmt;
    totalTax      += taxAmt;
    if (disc > 0) hasDiscount = true;

    const isIGST = it.igst_percent > 0 || it.igst_amount > 0;
    if (isIGST) {
      const r = it.igst_percent || tax;
      const a = it.igst_amount  || taxAmt;
      if (r > 0) igstBuckets[r] = (igstBuckets[r] || 0) + a;
    } else if (tax > 0) {
      const cr = it.cgst_percent || tax / 2;
      const sr = it.sgst_percent || tax / 2;
      const ca = it.cgst_amount > 0 ? it.cgst_amount : basic * cr / 100;
      const sa = it.sgst_amount > 0 ? it.sgst_amount : basic * sr / 100;
      cgstBuckets[cr] = (cgstBuckets[cr] || 0) + ca;
      sgstBuckets[sr] = (sgstBuckets[sr] || 0) + sa;
    }

    const cells = {
      sl:   i + 1,
      desc: it.item_name || DASH,
      uom:  it.uom_display || DASH,
      qty:  freeQty > 0 ? `${formatQty(qty)}\n(+${freeQty} free)` : formatQty(qty),
      mrp:  it.mrp ? Rs(it.mrp) : DASH,
      rate: Rs(rate),
      disc: pct(disc),
      gst:  pct(tax),
      amt:  Rs(basic + taxAmt),
    };
    return columns.map(c => cells[c.key]);
  });

  const emptyRow = columns.map(c => (c.key === 'desc' ? 'No line items found.' : ''));
  const base = tableStyles();

  // The ITEMS card wraps a table that may span pages, so its frame is drawn per page:
  // the title strip before the head row, the border once the last row on that page is
  // known. Stroke-only, so painting the border after the rows occludes nothing.
  const TABLE_LEFT = SIDE_M + 1.5;
  const TABLE_RIGHT = SIDE_M + U - 1.5;
  let itemsCardTop = y;

  doc.autoTable({
    startY: y + CARD_HEAD_H,
    head: [columns.map(c => c.label)],
    body: tableRows.length ? tableRows : [emptyRow],
    ...base,
    columnStyles: columns.reduce((acc, c, i) => {
      acc[i] = {
        cellWidth: c.w,
        ...(c.halign ? { halign: c.halign } : {}),
        ...(c.bold ? { fontStyle: 'bold' } : {}),
      };
      return acc;
    }, {}),
    tableWidth: U - 3,
    margin: { top: CONT_TOP + CARD_HEAD_H, left: TABLE_LEFT, right: TABLE_LEFT, bottom: 20 },
    showHead: 'everyPage',
    willDrawPage: (data) => {
      // The document's own page number, not the table-relative one: autotable can
      // push the table to a fresh page without bumping its counter when the head
      // will not fit below the cards.
      if (doc.internal.getCurrentPageInfo().pageNumber > 1) onNewPage(doc);
      itemsCardTop = data.cursor.y - CARD_HEAD_H;
      drawCardTitle(doc, SIDE_M, itemsCardTop, U, 'Items');
    },
    didDrawPage: (data) => {
      strokeCard(doc, SIDE_M, itemsCardTop, U, data.cursor.y + CARD_PAD_BOT - itemsCardTop);
    },
    didDrawCell: (data) => {
      if (data.column.index === 0) drawRowRule(doc, data, TABLE_LEFT, TABLE_RIGHT);
      if (data.section === 'body' && data.column.index === descIndex) {
        const it = items[data.row.index];
        if (it) {
          drawItemSubline(doc, data, [
            it.hsn_code && it.hsn_code !== DASH ? `HSN: ${it.hsn_code}` : null,
            it.manufacturer_name,
          ]);
        }
      }
    },
  });

  y = doc.lastAutoTable.finalY + CARD_PAD_BOT + CARD_GAP;

  // ── 5. AMOUNT IN WORDS + SUMMARY ───────────────────────────────────────────
  const subtotal = totalGross - totalDiscount;
  const grandTotal = subtotal + totalTax;

  const summaryRows = hasDiscount
    ? [
        ['Sub-Total (Items)', Rs(totalGross)],
        ['Discount (-)',      Rs(totalDiscount)],
        ['Net Taxable',       Rs(subtotal)],
      ]
    : [['Sub-Total', Rs(subtotal)]];

  const byRate = (buckets, label) =>
    Object.entries(buckets)
      .sort(([a], [b]) => Number(a) - Number(b))
      .map(([r, amt]) => [`${label} (${r}%)`, Rs(amt)]);

  const gstRows = [
    ...byRate(cgstBuckets, 'CGST'),
    ...byRate(sgstBuckets, 'SGST'),
    ...byRate(igstBuckets, 'IGST'),
  ];
  if (gstRows.length) summaryRows.push(...gstRows);
  else if (totalTax > 0) summaryRows.push(['Tax', Rs(totalTax)]);

  const wordsM = measureWordsCard(doc, CARD_W, numberToWords(grandTotal), po.remarks);
  const summaryH = measureSummary(summaryRows);
  const rowH = Math.max(wordsM.height, summaryH);

  y = ensureSpace(doc, y, rowH, onNewPage);
  drawWordsCard(doc, SIDE_M, y, CARD_W, rowH, 'Amount in Words & Remarks',
    wordsM.wordLines, wordsM.remarkLines);
  drawSummaryCard(doc, CARD_X_RIGHT, y, CARD_W, rowH, 'Summary',
    summaryRows, 'Grand Total', Rs(grandTotal));
  y += rowH + CARD_GAP;

  // ── 6. TERMS & CONDITIONS ──────────────────────────────────────────────────
  const terms = HOSPITAL_INFO.poTerms || [];
  if (terms.length) {
    const text = terms.map((t, i) => `${i + 1}.  ${t}`).join('\n');
    const termsM = measureLines(doc, text, U, 6.5);
    y = ensureSpace(doc, y, termsM.height, onNewPage);
    drawLines(
      doc,
      SIDE_M,
      drawCardFrame(doc, SIDE_M, y, U, termsM.height, 'Terms & Conditions'),
      termsM.lines,
      termsM.pitch,
      C.muted,
    );
    y += termsM.height;
  }

  // ── 7. SIGNATURE + FOOTER ──────────────────────────────────────────────────
  y = ensureSpace(doc, y, 18, onNewPage);
  drawSignatureRow(
    doc,
    y,
    `Prepared by  ·  ${displayOrDash(HOSPITAL_INFO.department)}`,
    'Authorised Signatory',
  );

  stampFooters(doc, identity, letterhead);
  return doc;
}

// ─── Blob / download helpers ──────────────────────────────────────────────────
export function getPoPdfFilename(po = {}) {
  return `${po.po_number || 'purchase-order'}.pdf`;
}

export function buildPoPdfBlob(data) {
  return createPoPdfDocument(data).output('blob');
}

export function downloadPoPdfFromData(data) {
  createPoPdfDocument(data).save(getPoPdfFilename(data?.po));
}

export function previewPoPdfInNewTab(data) {
  const url = URL.createObjectURL(buildPoPdfBlob(data));
  window.open(`${url}#zoom=page-fit`, '_blank', 'noopener');
}

export async function fetchPoPdfData(poId, scope = {}, extras = {}) {
  const [po, itemsRaw, catalog, uomsRaw] = await Promise.all([
    getPurchaseOrder(poId),
    getPurchaseOrderItems(poId, scope).catch(() => []),
    fetchItemCatalog().catch(() => null),
    getUoms().catch(() => []),
  ]);

  const uomMap = {};
  normalizeItemList(uomsRaw).forEach(u => {
    const id = u.uom_id || u.id;
    if (id) uomMap[id] = u.uom_name || u.uom_code || u.name || '';
  });

  const items = enrichItems(normalizeItemList(itemsRaw), catalog, uomMap);

  let vendor = extras.vendor || null;
  if (!vendor && po.vendor_id) {
    try { vendor = await getVendor(po.vendor_id); } catch { /* non-critical */ }
  }

  return {
    po,
    items,
    vendor,
    facilityLogo: extras.facilityLogo || null,
    letterhead: extras.letterhead,
  };
}

export async function buildPoPdfPackage(poId, scope = {}, extras = {}) {
  const data = await fetchPoPdfData(poId, scope, extras);
  const blob = buildPoPdfBlob(data);
  const url  = URL.createObjectURL(blob);
  return { data, blob, url, filename: getPoPdfFilename(data.po) };
}

export async function downloadPoPdf(poId, scope = {}, extras = {}) {
  const data = await fetchPoPdfData(poId, scope, extras);
  downloadPoPdfFromData(data);
}
